import { useEffect, RefObject } from "react";

export interface UseAutoScrollWithResizeOptions {
  /** Distance from bottom (in pixels) to trigger auto-scroll on resize. Default: 300 */
  threshold?: number;
  /** Scroll behavior for initial scroll. Default: "smooth" */
  initialBehavior?: ScrollBehavior;
  /** Scroll behavior for resize-triggered scrolls. Default: "instant" */
  resizeBehavior?: ScrollBehavior;
}

/**
 * Auto-scrolls a container to the bottom when dependencies change,
 * and watches for content size changes (like images loading) to maintain scroll position.
 *
 * @param containerRef - Reference to the scrollable container element
 * @param dependencies - Array of dependencies that trigger initial scroll (e.g., [turns.length, indicator])
 * @param options - Configuration options for scroll behavior
 *
 * @example
 * ```tsx
 * const dialogRef = useRef<HTMLElement>(null);
 * useAutoScrollWithResize(dialogRef, [turns.length, indicator], { threshold: 300 });
 * ```
 */
export const useAutoScrollWithResize = (
  containerRef: RefObject<HTMLElement>,
  dependencies: any[] = [],
  options: UseAutoScrollWithResizeOptions = {},
): void => {
  const {
    threshold = 300,
    initialBehavior = "smooth",
    resizeBehavior = "instant",
  } = options;

  useEffect(() => {
    const scrollToBottom = (behavior: ScrollBehavior = initialBehavior) => {
      requestAnimationFrame(() => {
        const el = containerRef.current;
        if (!el) return;

        const { scrollHeight, clientHeight } = el;
        const targetScroll = scrollHeight - clientHeight;
        el.scrollTo({ top: targetScroll, behavior });
      });
    };

    // Initial scroll when dependencies change
    scrollToBottom();

    // Watch for content size changes (like images loading) and auto-scroll
    const el = containerRef.current;
    if (!el) return;

    const resizeObserver = new ResizeObserver(() => {
      // Only auto-scroll if user is already near the bottom
      const { scrollTop, scrollHeight, clientHeight } = el;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      if (distanceFromBottom < threshold) {
        scrollToBottom(resizeBehavior);
      }
    });

    resizeObserver.observe(el);

    return () => {
      resizeObserver.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);
};
